/* eslint-disable no-useless-escape */

/* eslint-disable no-case-declarations */
import { match } from 'ts-pattern';

type VideoPlatform =
  | 'youtube'
  | 'vimeo'
  | 'googleDrive'
  | 'instagram'
  | 'tiktok';

export const SUPPORTED_PLATFORMS: Record<VideoPlatform, RegExp> = {
  youtube:
    /^(https?:\/\/)?(www\.)?(youtube\.com\/(watch\?v=|embed\/|shorts\/)|youtu\.be\/)[^&\s?]+/,
  vimeo: /^(https?:\/\/)?(www\.|player\.)?vimeo\.com\/(video\/|embed\/)?(\d+)/,
  googleDrive: /^(https?:\/\/)?(www\.)?drive\.google\.com\/file\/d\/[^\/\s?]+/,
  instagram:
    /^(https?:\/\/)?(www\.)?instagram\.com\/(?:[^\/]+\/)?(p|reel|tv)\/[^\/\s?]+/,
  tiktok:
    /^(https?:\/\/)?(www\.)?tiktok\.com\/(@[^\/]+\/video\/[^\/\s?]+|t\/[^\/\s?]+)/,
} as const;

type Platform = keyof typeof SUPPORTED_PLATFORMS;

export const getVideoPlatform = (url: string): VideoPlatform | null => {
  return Object.entries(SUPPORTED_PLATFORMS).find(([_, regex]) =>
    regex.test(url),
  )?.[0] as VideoPlatform | null;
};

export const getEmbedUrl = (url: string) => {
  const platform = getVideoPlatform(url);
  if (!platform) return url;

  const videoId = extractVideoId(url, platform);
  if (!videoId) return url;

  return match(platform)
    .with('youtube', () => `https://www.youtube.com/embed/${videoId}`)
    .with(
      'vimeo',
      () =>
        `https://player.vimeo.com/video/${videoId}?title=0&byline=0&portrait=0`,
    )
    .with(
      'googleDrive',
      () => `https://drive.google.com/file/d/${videoId}/preview`,
    )
    .with('instagram', () => {
      const match = url.match(
        /instagram\.com\/(?:[^\/]+\/)?(p|reel|tv)\/([^\/\s?]+)/,
      );
      if (match) {
        const [_, type, id] = match;
        return `https://www.instagram.com/${type}/${id}/embed`;
      }
      return url;
    })
    .with('tiktok', () => `https://www.tiktok.com/embed/v2/${videoId}`)
    .exhaustive();
};

// Helper function to extract video IDs
const extractVideoId = (url: string, platform: Platform): string | null => {
  switch (platform) {
    case 'youtube':
      // Handle youtube.com/watch, youtube.com/embed, youtube.com/shorts, and youtu.be URLs
      const youtubeMatch = url.match(
        /(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([^&\s?]+)/,
      );
      if (!youtubeMatch) return null;
      // Extract just the video ID without any query parameters
      const videoId = youtubeMatch[1].split('?')[0];
      return videoId;
    case 'vimeo':
      // Handle vimeo.com/video/, vimeo.com/embed/, and direct vimeo.com/ URLs
      const vimeoMatch = url.match(
        /vimeo\.com(?:\/(?:video\/|embed\/)?)?(\d+)/,
      );
      return vimeoMatch?.[1] || null;
    case 'googleDrive':
      // Handle URLs with query parameters
      const driveMatch = url.match(/\/file\/d\/([^\/\s?]+)/);
      return driveMatch?.[1] || null;
    case 'instagram':
      const instaMatch = url.match(
        /instagram\.com\/(?:[^\/]+\/)?(p|reel|tv)\/([^\/\s?]+)/,
      );
      if (!instaMatch) return null;
      const [_, type, id] = instaMatch;
      // Return both type and ID to construct proper embed URL
      return `${type}/${id}`;
    case 'tiktok':
      const tiktokMatch = url.match(
        /tiktok\.com\/(?:@[^\/]+\/video\/([^\/\s?]+)|t\/([^\/\s?]+))/,
      );
      return tiktokMatch?.[1] || tiktokMatch?.[2] || null;
    default:
      return null;
  }
};

// Function to check if a video exists
const checkVideoExists = async (
  url: string,
  platform: Platform,
): Promise<boolean> => {
  const videoId = extractVideoId(url, platform);
  if (!videoId) return false;

  try {
    switch (platform) {
      case 'youtube':
        // Check YouTube video existence using thumbnail method
        return new Promise((resolve) => {
          const img = new Image();
          img.src = `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`;
          img.onload = function () {
            // A width of 120 indicates default thumbnail (video doesn't exist)
            resolve((img as HTMLImageElement).width !== 120);
          };
          img.onerror = () => resolve(false);
        });

      case 'vimeo':
        // Check Vimeo video existence using oEmbed endpoint
        try {
          const response = await fetch(
            `https://vimeo.com/api/oembed.json?url=https://vimeo.com/${videoId}`,
          );
          return response.status === 200;
        } catch {
          return false;
        }

      case 'googleDrive':
        // Check Google Drive file existence using preview endpoint
        try {
          const response = await fetch(
            `https://drive.google.com/file/d/${videoId}/preview`,
            { method: 'HEAD' },
          );
          return response.status === 200;
        } catch {
          return false;
        }

      case 'instagram':
        // Just validate the format since we can't check existence without auth
        return videoId.includes('/');
      case 'tiktok':
        // Accept any URL that matches the TikTok format and has a video ID
        return videoId.length > 0;
      default:
        return false;
    }
  } catch (error) {
    return false;
  }
};

const isUrl = (string: string) => {
  try {
    return Boolean(new URL(string));
  } catch (e) {
    return false;
  }
};

export const validateVideoUrl = async (
  url: string,
): Promise<{ isValid: boolean; error?: string }> => {
  if (!url.startsWith('https://')) {
    return { isValid: false, error: 'URL must start with https://' };
  }

  if (!isUrl(url)) {
    return { isValid: false, error: 'Enter a valid link.' };
  }

  const platform = Object.entries(SUPPORTED_PLATFORMS).find(([_, regex]) =>
    regex.test(url),
  )?.[0] as Platform;

  if (!platform) {
    return {
      isValid: false,
      error:
        'Only YouTube, Vimeo, Google Drive, Instagram, and TikTok videos are supported.',
    };
  }

  const videoId = extractVideoId(url, platform);
  if (!videoId) {
    return { isValid: false, error: 'Invalid video ID.' };
  }

  const videoExists = await checkVideoExists(url, platform);
  if (!videoExists) {
    return {
      isValid: false,
      error: 'This video does not exist or is not accessible.',
    };
  }

  return { isValid: true };
};
